import {
  Deal,
  getConvertedPriceRegion,
  getDealSettings,
  OrderValues,
  RemoteConfig,
  Step,
} from '@ca/common/types'
import { Discounts } from '@order/utils/discounts/Discounts'
import { debugSteps } from '@order/utils/getDebugLog'
import validateCondition, {
  ValidationResult,
} from '@order/utils/validateCondition'
import { StepCtx } from '@order/steps/useStepCtx'
import type { i18n } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { resolveCustomOptions } from '../utils/resolveCustomOptions'
import { ResolvedVariant, resolveVariants } from '../utils/resolveVariants'
import {
  buildStepTlVariants,
  resolveStepItemKey,
} from '../utils/stepTranslations'
import { useActiveDeal } from './useDeal'
import { validateOrderValues } from '@order/utils/checkOrderValues'
import AttributeUtils from '@order/utils/AttributeUtils'

const getStepTlKey = (step: Step) => {
  if (step.variable) {
    return `steps:${step.variable}`
  }
  // Fall back to step name if no variable is set
  return `steps:${step.name}.title`
}

const getOptionTls = (prefix: string, i18n: i18n, variants: string[] = []) => {
  const lookup = [...variants.map((v) => `${prefix}.${v}`), prefix]

  const titleKey = resolveStepItemKey({
    prefix,
    tlVariants: variants,
    key: 'title',
    i18n,
  })
  const descKey = resolveStepItemKey({
    prefix,
    tlVariants: variants,
    key: 'desc',
    i18n,
  })
  const labelKey = resolveStepItemKey({
    prefix,
    tlVariants: variants,
    key: 'label',
    i18n,
  })
  const stepDescKey = resolveStepItemKey({
    prefix,
    tlVariants: variants,
    key: 'stepDesc',
    i18n,
  })
  const priceValueKey = resolveStepItemKey({
    prefix,
    tlVariants: variants,
    key: 'price.value',
    i18n,
  })

  return {
    title: titleKey ? i18n.t(titleKey) : '',
    desc: descKey ? i18n.t(descKey) : '',
    label: labelKey ? i18n.t(labelKey) : undefined,
    priceValue: priceValueKey ? i18n.t(priceValueKey) : undefined,
    stepDesc: stepDescKey,
    lookup,
  } as const
}

const getDiscountedPrice = (
  item: ResolvedVariant,
  {
    deal,
    config,
    values,
  }: {
    deal: Deal | undefined
    config: RemoteConfig
    values: OrderValues
  }
) => {
  const price = item.active.price
  const variable = item.variable
  const value = item.active.value

  const itemValues = {
    refraction: values.refraction,
    values: {
      ...values.values,
      [variable]: value,
    },
  }

  const discount = deal?.discounts
    .filter((d) => d.scope === 'variable' && d.variable === variable)
    .filter(
      (d) =>
        !d.condition ||
        validateOrderValues(config.conditions[d.condition], itemValues, config)
          .result
    )

  const settings = getDealSettings(deal)
  const converted = getConvertedPriceRegion(price, settings)
  const wrapped = Discounts.wrapRegion(converted, config.region)
  return Discounts.applyDiscounts(wrapped, discount)
}

export const getStepOption = (
  item: ResolvedVariant,
  {
    stepId,
    i18n,
    getConfirm,
    deal,
    config,
    flowName,
    values,
  }: {
    deal: Deal | undefined
    config: RemoteConfig | null
    values: OrderValues
    stepId: string | number
    i18n: i18n
    flowName?: string
    getConfirm?: (item: ResolvedVariant) => ValidationResult | undefined
  }
) => {
  if (!config) {
    throw new Error('Config is required')
  }

  const step = config?.steps[stepId]
  if (!step) {
    throw new Error(`Step ${stepId} not found`)
  }

  const tlVariants = buildStepTlVariants({
    translationPath: item.active.translationPath,
    flowName,
    type: values.values['type'],
  })

  // Make attributes inherit from base if not set on active
  const attributes = item.active.attributes?.length
    ? item.active.attributes
    : item.base.attributes

  return {
    ...item,
    active: {
      ...item.active,
      ...getOptionTls(
        `${getStepTlKey(step)}.items.${item.active.value}`,
        i18n,
        tlVariants
      ),
      price: getDiscountedPrice(item, {
        deal,
        config,
        values,
      }),
      pricePrefix:
        item.active.price.prefix !== 'none' ? item.active.price.prefix : null,
      priceDisplayOnly: item.active.price.displayOnly,
      image: item.active.image?.url || item?.base.image?.url,
      imageProps: item.active.imageProps ?? item.base.imageProps,
      confirm: getConfirm?.(item) || undefined,
      cta: item.active.cta,
      // color: item.active.color || item.base.color,
      colors: item.active.colors?.length
        ? item.active.colors
        : item.base.colors,
      attributes: AttributeUtils.resolveAttributes({
        attributes: attributes || [],
        config,
        values,
      }),
    },
  }
}

export type StepOption = ReturnType<typeof getStepOption>

const useOrderStep = (id: string, ctx: StepCtx) => {
  const { state, config, values, refraction } = ctx
  const deal = useActiveDeal()
  const { t, i18n } = useTranslation(['steps'])
  const step = config?.steps[id]

  const memoizedStep = useMemo(() => {
    if (!step || !refraction) {
      return null
    }

    const resolvedVariants = resolveVariants({
      config,
      refraction,
      step,
      values,
    })

    const customOptions = resolveCustomOptions({
      config,
      refraction,
      step,
      values,
    })

    // const video = step.variable
    //   ? config.videos[i18n.language][step.variable]?.variants?.find((variant) =>
    //       checkRequirements(variant, values)
    //     )
    //   : null

    const getConfirm = (item: ResolvedVariant) => {
      if (!item.active.confirm) {
        return undefined
      }
      const res = validateCondition(
        config.conditions[item.active.confirm],
        {
          ...values,
          ref: refraction,
        },
        config.conditions
      )
      return res
    }

    const res = {
      ...step,
      title: t(`${getStepTlKey(step)}.title`),
      desc: t(`${getStepTlKey(step)}.desc`),
      items: resolvedVariants.map((item) =>
        getStepOption(item, {
          stepId: step.id,
          getConfirm,
          i18n,
          flowName: ctx.flowName,
          deal,
          config,
          values: {
            values,
            refraction,
          },
        })
      ),
      video: null,
      customOptions: customOptions.map((option) => ({
        ...option,
        ...getOptionTls(`customOptions:${option.name}`, i18n),
        price: Discounts.wrapRegion(option.price, config.region),
      })),
      states: state.states,
      values,
    }
    debugSteps.log('Step item', res, { values, refraction })

    return res
  }, [
    config,
    ctx.flowName,
    deal,
    i18n,
    refraction,
    state.states,
    step,
    t,
    values,
  ])

  return memoizedStep
}

export default useOrderStep
